import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useNewOnboardingEligibility } from '../../hooks/useNewOnboardingEligibility';
import { ROUTES_PATH } from '../../constants/Routes';
import { useOabEligibility } from '../../services/oabEligibility';
import { professionToOnboardingBranch } from '../../services/tracking/onboarding/onboardingBranch';
import { UserPreferences } from '../../services/userPreferences/types';
import { OnboardingToolsStatus } from './types';

export function useOnboardingTools() {
  const history = useHistory();
  const {
    baseEligible,
    completed: onboardingToolsCompleted,
    isSubscriptionLoading,
    isPollingCredits,
    flagResolved,
    isPreferencesLoading,
    isStudentLoading,
    preferences,
    updateUserPreferencesAsync,
  } = useNewOnboardingEligibility('onboardingToolsCompleted');

  const prefs = preferences as UserPreferences;
  const oabSkipped = Boolean(prefs.onboardingOabSkipped);
  // Skipped-OAB users come through the photo step; everyone else through the profile.
  const priorStepDone = Boolean(
    oabSkipped
      ? prefs.onboardingPhotoCompleted || prefs.onboardingProfileCompleted
      : prefs.onboardingProfileCompleted,
  );
  const { profession, isEligibilityResolved } = useOabEligibility({
    enabled: baseEligible,
  });

  const status: OnboardingToolsStatus = useMemo(() => {
    if (
      isStudentLoading ||
      isSubscriptionLoading ||
      !flagResolved ||
      isPreferencesLoading
    ) {
      return 'loading';
    }

    if (!baseEligible) {
      return 'ineligible';
    }

    if (!priorStepDone) {
      return 'prior_pending';
    }

    if (onboardingToolsCompleted) {
      return 'completed';
    }

    return 'ready';
  }, [
    isStudentLoading,
    isSubscriptionLoading,
    flagResolved,
    isPreferencesLoading,
    baseEligible,
    priorStepDone,
    onboardingToolsCompleted,
  ]);

  const selectTool = useCallback(
    async (route: string, onBeforeNavigate?: () => void) => {
      await updateUserPreferencesAsync({ onboardingToolsCompleted: true });
      onBeforeNavigate?.();
      history.push(route);
    },
    [updateUserPreferencesAsync, history],
  );

  const priorStepPath = oabSkipped
    ? ROUTES_PATH.ONBOARDING.PHOTO
    : ROUTES_PATH.ONBOARDING.OFFICE;

  const goBack = useCallback(() => {
    history.push(priorStepPath);
  }, [history, priorStepPath]);

  return {
    status,
    isPollingCredits,
    selectTool,
    goBack,
    oabSkipped,
    priorStepPath,
    isEligibilityResolved,
    branch: professionToOnboardingBranch(profession),
  };
}
